// Lib
import _ from "lodash";
import { oc } from "ts-optchain";

// Vue
import Vue from "vue";
import { mapGetters } from "vuex";
import { constants }    from "@/const";

// Mixins
import { stringHelper, partsStoreOrderDetailsMixin } from "@/components/mixins";

// Services
import { DataUtil, ErrorLogService, ShoppingCartService, DealerUtil, CurrencyService } from "@/services";
import { CartService } from "./Services/CartService";
import { FeatureFlagService }   from "@/services/FeatureFlagService"

// Components
import Step1 from "./Step1/Step1.vue";
import Step2 from "./Step2/Step2.vue";
import Step3 from "./Step3/Step3.vue";
import { IGridListRowItem } from "@/components/Elements/PartsListGrid/PartsListGrid";

import {
  LoadingSpinner
} from "@/components";

// Types
import { 
  IDealerInfo, 
  IOrderOptionsDto, 
  IShoppingCartListResponseDto, 
  PartValidationReason, 
  IOrderResponseDto, 
  IFreightChargeLogDto } from "@/types";
import { CartStepName, IShoppingCartContext, OrderValidationErrorReason} from "./Types";

const isShoppingCart = true;

export default Vue.extend({
  name: "Cart",
  components: {
    Step1,
    Step2,
    Step3,
    LoadingSpinner
  },
  mixins: [stringHelper, partsStoreOrderDetailsMixin],
  data: () => ({
    isLoading: true,
    context: null,
    activeView: "DEFAULT",
    activeStep: "STEP_1",
    navigatingToCartStep: false,
    ExxonInfo: null,
    partsStoreOrderId: {
      type: String,
      default: null,
    }
  }),
  computed: {
    ...mapGetters([
      "getContentItem",
      "activeAccountId",
      "user",
      "audienceIds",
      "cartSummary",
      "availableDealerCodes",
      "canPlaceOrders",
      "getRecentComponentName",
      "billingCurrencyCode"
    ]),
    
    validationErrorMessages() {
      var errors = this.context?.cartData?.ValidationResults.ValidationErrors ?? [];
      var formattedMessages: any = [];
      var filteredErrors = errors.filter(e => e.ValidationErrorCode != "RPI60");

      if (errors.length < 1) {
        return errors;
      }
      filteredErrors.forEach((error: any) => {
        var errorMessage = error.Message;
        var splitErrorMessage = error.Message.split(" ");
        var errMessage: any = "";
        switch (error.Reason) {

          case OrderValidationErrorReason.DealerNotFound:
            errMessage = {
              PartNumber: null,
              Message: "Error: Dealer Not Found. [DealerNotFound]"
            }
            break;
          case OrderValidationErrorReason.DealerOnCreditHold:
            errMessage = {
              PartNumber: null,
              Message: "Error: Dealer On Credit Hold. [DealerOnCreditHold]"
            }
            break;
          case OrderValidationErrorReason.InvalidModelAndSerialNumber:
            errMessage = {
              PartNumber: null,
              Message: "Error: Invalid Model and Serial Number. [InvalidModelAndSerialNumber]"
            }
            break;
          case OrderValidationErrorReason.InvalidPONumber:
            //This one is ignored, we're handling it on the front end
            break;
          case OrderValidationErrorReason.InvalidShippingAddress:
            errMessage = {
              PartNumber: null,
              Message: "Error: Invalid Shipping Address. [InvalidShippingAddress]"
            }
            break;
          case OrderValidationErrorReason.InvalidShippingCode:
            errMessage = {
              PartNumber: null,
              Message: "Error: Invalid Shipping Code. [InvalidShippingCode]"
            }
            break;
          case OrderValidationErrorReason.ItemDiscontinued:
            errMessage = {
              PartNumber: splitErrorMessage[1],
              Message: ` - This item has been discontinued.
              Please call Dealer Support at (800) 258-6232 with truck model and serial
               to see if a replacement item is available. [ItemDiscontinued]`
            };
            break;
          case OrderValidationErrorReason.ItemNotAvailable:
            errMessage = {
              PartNumber: splitErrorMessage[1],
              Message: ` is currently not available.
              Please remove it from your cart to continue your order.
              You May Contact Dealer Support for further information at (800) 258-6232. [ItemNotAvailable]`
            };
            break;
          case OrderValidationErrorReason.ItemQuantityMax:
            errMessage = {
              PartNumber: splitErrorMessage[1],
              Message: ` has exceeded maximum quantity. Please remove it from your cart to continue your order. [ItemQuantityMax]`
            };
            break;
          case OrderValidationErrorReason.ItemQuantityStock:
            errMessage = {
              PartNumber: splitErrorMessage[1],
              Message: ` experienced an error. Please remove it from your cart to continue your order. [ItemQuantityStock]`
            };
            break;
          case OrderValidationErrorReason.ItemQuantityEmergency:
            errMessage = {
              PartNumber: splitErrorMessage[1],
              Message: ` experienced an error. Please remove it from your cart to continue your order. [ItemQuantityEmergency]`
            };
            break;
          case OrderValidationErrorReason.InvalidItemNumber:
            errMessage = {
              PartNumber: splitErrorMessage[1],
              Message: ` is an invalid item number. Please remove it from your cart to continue your order. [InvalidItemNumber]`
            };
            break;
          case OrderValidationErrorReason.ItemNotOrderable:
            errMessage = {
              PartNumber: splitErrorMessage[1],
              Message: ` is not orderable. Please remove it from your cart to continue your order. [ItemNotOrderable]`
            };
            break;
          case OrderValidationErrorReason.ItemSoftFreeze:
            errMessage = {
              PartNumber: splitErrorMessage[1],
              Message: ` is frozen and may only be ordered by calling Dealer Support at (800) 258-6232. [ItemSoftFreeze]`
            };
            break;
          case OrderValidationErrorReason.ItemHardFreeze:
            errMessage = {
              PartNumber: splitErrorMessage[1],
              Message: ` is frozen
              and cannot be ordered at this time.
              Please remove it from your cart to continue your order. [ItemHardFreeze]`
            };
            break;
          case OrderValidationErrorReason.ItemChannelAuthorization:
            errMessage = {
              PartNumber: splitErrorMessage[1],
              Message: ` has limited NPR
              data available due to channel authorization.
              Please remove it from your cart to continue your order. [ItemHardFreeze]`
            };
            break;
          case OrderValidationErrorReason.McfnetDown:
            errMessage = {
              PartNumber: null,
              Message: `An error has occurred. Please Contact Dealer Support at (800) 258-6232. [MND]`
            };
            break;
          case OrderValidationErrorReason.BulkPricingAvailable:
            errMessage = {
              PartNumber: null,
              Message: `An error has occurred. Please Contact Dealer Support at (800) 258-6232. [BPA]`
            };
            break;
          case OrderValidationErrorReason.InvalidExxonMobilMixMatchQuantities:
            var index = 0;
            //Iterate through the mix match results and find the exxon mixmatch (could have multiple mixmatchresults in cart)
            for (var i = 0; i < this.context?.cartData?.MixMatchResults.length; i++){
              if (this.context?.cartData?.MixMatchResults[i].Brand.includes("Exxon")){
                index = i;
              }
            }
            //Setting exxon mixmatch
            const mixMatch = this.context?.cartData?.MixMatchResults[index];
            //Setting field values required for message
            var minimum = mixMatch.RequiredMinimum;
            var amountInCart = mixMatch.AmountInCart;
            var qtyNeeded = (minimum - amountInCart).toFixed(2);
            var unitOfMeasure = mixMatch.UnitOfMeasure;

            var message = this.format(this.getContentItem("ExxonMixMatchMessage"),minimum,amountInCart,qtyNeeded,unitOfMeasure)
            //If distributor info is found then add contact info to the error message
            if(this.ExxonInfo){
              message += this.format(this.getContentItem("ExxonInfo"),this.ExxonInfo.RepName,this.ExxonInfo.RepEmail, this.ExxonInfo.RepPhoneNumber);
            }
            errMessage = {
              PartNumber: null,
              Message: message
            };
            break;
          case OrderValidationErrorReason.InvalidSpectrumMixMatchQuantities:
            errMessage = {
              PartNumber: null,
              Message: `An error has occurred.
              Please Contact Dealer Support at (800) 258-6232. [InvalidSpectrumMixMatchQuantities]`
            };
            break;
          case OrderValidationErrorReason.InvalidStarterBatteryMixMatchQuantities:
            errMessage = {
              PartNumber: null,
              Message: `An error has occurred.
              Please Contact Dealer Support at (800) 258-6232. [InvalidStarterBatteryMixMatchQuantities]`
            };
            break;
          case OrderValidationErrorReason.ItemMinimumOrderQuantity:
            errMessage = {
              PartNumber: splitErrorMessage[1],
              Message: `has a minimum order quantity of ${splitErrorMessage[2]}.
              Please change the quantity of this item to a multiple of ${splitErrorMessage[2]} to continue your order.`
            };
            break;
          case OrderValidationErrorReason.ItemCaseQuantity:
            errMessage = {
              PartNumber: splitErrorMessage[1],
              Message: `has a case quantity of ${splitErrorMessage[2]}.
              Please change the quantity of this item to a multiple of ${splitErrorMessage[2]} to continue your order.`
            };
            break;
          case OrderValidationErrorReason.MaterialNotListed:
            errMessage = {
              PartNumber: splitErrorMessage[1],
              Message: ` - Material is not listed and therefore not allowed. Please remove it from your cart to continue your order. [MaterialNotListed].`
            };
            break;
          case OrderValidationErrorReason.SoldToCustomerBlocked:
            errMessage = {
              PartNumber: null,
              Message: `Sold To Customer Is Blocked. [SoldToCustomerBlocked]`
            };
            break;
          case OrderValidationErrorReason.SoldToMissing:
            errMessage = {
              PartNumber: null,
              Message: `An error has occurred. Please Contact Dealer Support at (800) 258-6232. [SoldToMissing]`
            };
            break;
          case OrderValidationErrorReason.SoldToCustomerInvalid:
            errMessage = {
              PartNumber: null,
              Message: `An error has occurred. Please Contact Dealer Support at (800) 258-6232. [SoldToCustomerInvalid]`
            };
            break;
          case OrderValidationErrorReason.ShipToCustomerBlocked:
            errMessage = {
              PartNumber: null,
              Message: `An error has occurred. Please Contact Dealer Support at (800) 258-6232. [STCB]`
            };
            break;
          case OrderValidationErrorReason.FinalToCustomerMissing:
            errMessage = {
              PartNumber: null,
              Message: `An error has occurred. Please Contact Dealer Support at (800) 258-6232. [FinalToCustomerMissing]`
            };
            break;
          case OrderValidationErrorReason.MissingShipViaWarehouseCa:
            errMessage = {
              PartNumber: null,
              Message: `An error has occurred. Please Contact Dealer Support at (800) 258-6232. [MissingShipViaWarehouseCa]`
            };
            break;
          case OrderValidationErrorReason.InvalidShipViaWarehouseIn:
            errMessage = {
              PartNumber: null,
              Message: `An error has occurred. Please Contact Dealer Support at (800) 258-6232. [InvalidShipViaWarehouseIn]`
            };
            break;
          case OrderValidationErrorReason.InvalidFinalTo:
            errMessage = {
              PartNumber: null,
              Message: `An error has occurred. Please Contact Dealer Support at (800) 258-6232. [InvalidFinalTo]`
            };
            break;
          case OrderValidationErrorReason.NoFinalToCustomer:
            errMessage = {
              PartNumber: null,
              Message: `An error has occurred. Please Contact Dealer Support at (800) 258-6232. [NoFinalToCustomer]`
            };
            break;
          case OrderValidationErrorReason.ECCOPackageMixMatchTotalValue:
            errMessage = {
              PartNumber: null,
              Message: errorMessage
            };
            break;
          case OrderValidationErrorReason.OfflineToWarehouseSystems:
            this.context.isOfflineToWarehouseSystems = true;
            this.context.showOfflineToWarehouseMessageHeader = true;
            break;
          case OrderValidationErrorReason.OfflineToSap:
            var isOfflineOrderFeatureEnabled = FeatureFlagService.getFeatureEnabled(constants.FEATURE_NAMES.OFFLINE_ORDER_PROCESSING);
            this.context.isOfflineToSap = isOfflineOrderFeatureEnabled;
            this.context.showOfflineToWarehouseMessageHeader = true;
            break;
          case OrderValidationErrorReason.PicCodeMinPriceAmount:
            let minimumOrderAmount = CurrencyService.formatCurrency(splitErrorMessage[2], this.billingCurrencyCode);             
            errMessage = {
              PartNumber: splitErrorMessage[1],
              Message: `manufacturer has a minimum order amount of ${minimumOrderAmount}.
              Please change the currency amount of this and/or other similar items by this manufacturer to ${minimumOrderAmount} or greater to continue your order.`
            };
            break;
          default:
            errMessage = {
              PartNumber: null,
              Message: error.Message
            };
            break;
        }

        if(errMessage != ""){
          formattedMessages.push(errMessage);
        }
      });

      return formattedMessages;
    },
  },
  watch: {},
  created() {
    this.activeView = "DEFAULT";
    this.activeStep = "STEP_1";
    this.partsStoreOrderId = this.$route.query.partsStoreOrderId

    if (this.partsStoreOrderId != null && this.partsStoreOrderId != "") {
      this.resetRecentComponentData();
    }
    const hasAccessToAllBranches = DealerUtil.hasAccessToAllBranches(this.availableDealerCodes, this.user);
    
    const branch = this.currentDealerSelection = DealerUtil.determineCurrentDealerSelection(
      this.availableDealerCodes,
      this.user,
      hasAccessToAllBranches,
      isShoppingCart
    );

    this.context = {
      // Input
      accountId: this.activeAccountId,
      user: this.user,
      userAudiences: this.audienceIds,
      branch,
      partsStoreOrderId: this.partsStoreOrderId,
      getContentItem: this.getContentItem,
      format: this.format,
      formatNumber: DataUtil.formatNumber,

      // State
      isLoading: true,
      canProceedToStep2: false,
      canProceedToStep3: false,

      isOfflineToSap: false,
      isOfflineToWarehouseSystems: false,

      canSeeAnyPrice: this.canSeeAnyPrice,
      isPriceVisible: this.isPriceVisible,
      billingCurrencyCode: this.billingCurrencyCode,

      orderType: ShoppingCartService.getOrderTypePref(),
      promoCodes: ShoppingCartService.getAppliedPromoCodes(),
      
      mixMaxValidationOverride: false,

      //Freight Charge Log Dto
      freightChargeLogDto: undefined,
      isPartsStoreOrder: this.getRecentComponentName === "PartsStoreOrderDetails"
    };
    this.getExxonInfo()
    .then(response => this.ExxonInfo = response.data)
    .catch(err => ErrorLogService.LogException(err));
  },
  async mounted() {
    try {
      await this.loadCartSummary();
      await this.loadCartData();
    } catch(err) {
      ErrorLogService.LogException(err);
      this.$store.commit("pushMessage", {
        header: "Error Loading Cart",
        message: "There was an error loading your shopping cart. Please try emptying your cart in Account Details. If you are still experiencing this issue contact Dealer Support.",
        IsError: true
      });
    } finally {
      this.context.isLoading = false;
    }
  },
  methods: {
    async loadCartSummary() {
      const context = this.context as IShoppingCartContext;

      if (this.cartSummary && this.cartSummary.Id) {
        this.context = {
          ...context,
          cartId: this.cartSummary.Id,
        };
        return;
      }

      const cartSummary = await ShoppingCartService.GetShoppingCartSummary({
        accountId: context.accountId,
        userId: context.user.UserId,
      });

      this.context = {
        ...context,
        cartId: cartSummary.Id,
      };

      this.$store.commit("SET_CART_SUMMARY", cartSummary);
    },

    async loadCartData() {
      const context = this.context as IShoppingCartContext;
      const input = {
        accountId: context.accountId,
        userId: context.user.UserId,
        userAudiences: context.userAudiences,
        promoCodes: context.promoCodes,
        orderType: context.orderType,
        currencyDecimalDigits: 2,
        isPartsStoreOrder: context.isPartsStoreOrder,
      };

      const cartData = await ShoppingCartService.GetValidatedShoppingCartItems(input);
      this.setCartData(cartData);
    },

    async orderTypeChanged(orderType: string) {
      try {
        this.context = {
          ...this.context,
          isLoading: true,
          orderType,
        };

        await this.loadCartData();
      } finally {
        this.context.isLoading = false;
      }
    },

    async promoCodesChanged(promoCodes: string[]) {
      try {
        this.context = {
          ...this.context,
          isLoading: true,
          promoCodes,
        };
        await this.loadCartData();
      } finally {
        this.context.isLoading = false;
      }
    },

    async rowUpdated(row: any) {

      const context = this.context as IShoppingCartContext;
      const rq = {
        accountId: context.accountId,
        userId: context.user.UserId,
        userAudiences: context.userAudiences,
        promoCodes: context.promoCodes,
        orderType: context.orderType,
        partListId: context.cartId,
        itemId: row.Id,
        item: {
          itemId: row.Id,
          dealerPartNumber: row.DealerSku || "",
          oemId: row.Oem.Id,
          oemPartCode: row.OemPartCode || "",
          quantity: row.Quantity || 1,
          stockingLocation: row.StockingLocation || "",
        },
        currencyDecimalDigits: 2,
      };  

      const response = await ShoppingCartService.EditShoppingCartItem(rq);

      const responseItems = oc(response).Items([]);

      const currentItemState = _.cloneDeep(this.context.cartData.Items) as IGridListRowItem[];

      const updatedCartData = {
        ...response,
        Items: responseItems.map((item) => {
          const currentItem = currentItemState.find((x) => x.Id === item.Id);

          currentItem.newStockingLocation = currentItem.StockingLocation
          currentItem.newDealerSku = currentItem.DealerSku

           // fixes a memory bug for BULK pricing where it kept the previous value in memory when doing an edit of qty from the cart
          if(item.UnitPrice === currentItem.DiscountedUnitPrice || typeof currentItem.DiscountedUnitPrice == 'undefined') {
            currentItem.DiscountedUnitPrice = void 0; // keep it undefined
          } else {
            currentItem.DiscountedUnitPrice = item.UnitPrice;
          }

          if(item.TotalPrice === currentItem.DiscountedTotalPrice || typeof currentItem.DiscountedTotalPrice == 'undefined') {
            currentItem.DiscountedTotalPrice = void 0; // keep it undefined
          } else {
            currentItem.DiscountedTotalPrice = item.TotalPrice;
          }

          return {
            ...currentItem,
            ...item,
          };
        }),
      };

      this.setCartData(updatedCartData);
    },

    setCartData(cartData: IShoppingCartListResponseDto) {
      const context = this.context as IShoppingCartContext;

      if (cartData) {
        cartData.Items.forEach((element: any) => {
          const item = element as any;

          if (item.PartValidation && item.PartValidation.ValidationReason !== PartValidationReason.IsValid) {
            item._showDetails = true;
          }
          if(this.activeStep === "STEP_1" && item.StatusMessage.LongMessage){
            // If we're in step 1 we want to show the long status message for the description
            item.PartDescription = item.StatusMessage.LongMessage
          }
          if (DataUtil.isLikelyToBeLocalizedString(item.PartDescription)) {
            item.PartDescription = this.getContentItem(item.PartDescription);
          }
        });
      }

      this.context = {
        ...context,
        cartData,
        canProceedToStep2: CartService.canProceedToStep2(context, cartData),
        canProceedToStep3: CartService.canProceedToStep3(context, cartData, false, false),
        canSubmitOrder: CartService.canSubmitOrder(context, cartData, false, false),
      };
    },

    setOrderOptions(orderOptions: IOrderOptionsDto) {
      this.context.orderOptions = orderOptions;
    },

    setBranch(branch: IDealerInfo) {
      this.context.branch = branch;
      // Set the cart context accountid to the selected branch
      this.context.accountId = branch.AccountId;
      this.loadCartData();
    },

    setWarehouses(warehouses: any) {
      for (let i = 0; i < warehouses.length; i++){
        let setWarehouse = warehouses[i].options.filter(warehouse => warehouse.value === warehouses[i].value);
        // prevent js console errors when there isn't a match caused when the `All Warehouses` selection causes 
        // an individual warehouse location to be blank. Just break the loop and continue.
        if (setWarehouse.length === 0) { continue }; 

        warehouses[i].carrierDescription = setWarehouse[0].carrierDescription;
        warehouses[i].carrierId = setWarehouse[0].carrierId;
      }

      this.context.warehouses = warehouses;
    },

    setOrderSummary(orderSummary: IOrderResponseDto[]) {
      this.context.orderSummary = orderSummary;
    },

    rowSelectionUpdated(row: IGridListRowItem) {
      this.context.cartData.Items = this.context.cartData.Items.map((item: IGridListRowItem) => {
        if (item.Id === row.Id) {
          return {
            ...item,
            ...row,
          };
        }
        return { ...item };
      });
    },

    navigateToStep(stepName: CartStepName) {
      this.navigatingToCartStep = true;
      const context = this.context as IShoppingCartContext;

      switch (stepName) {
        case CartStepName.Step1:
          this.navigatingToCartStep = false;
          this.activeStep = stepName;
          break;
        case CartStepName.Step2:
          if (context.canProceedToStep2) {
            this.navigatingToCartStep = false;
            this.activeStep = stepName;
          }
          break;
        case CartStepName.Step3:
          if (context.canProceedToStep3) {
            this.navigatingToCartStep = false;
            this.activeStep = stepName;
          }
          break
        case CartStepName.ThankYou:
          this.navigatingToCartStep = false;
          this.activeStep = stepName;
          this.$router.push({ name: 'ShoppingCartThankYou', params: { context: context } })
          break;
      }
    },

    showMessage(msg: string) {
      this.$store.commit("pushTimedMessage", {
        header: msg,
        IsError: false,
        seconds: 3,
      });
    },

    showError(err: any) {
      const msg = ErrorLogService.LogAndGetErrorMessage(err);

      this.$store.commit("pushTimedMessage", {
        header: msg,
        IsError: true,
        seconds: 15,
      });
    },

    updateThirdParty(thirdPartyData: any) {
      this.context.thirdPartyOptions = thirdPartyData;
    },

    setFreightCharge(freightCharge: object) {
      this.context.freightChargeLogDto = freightCharge;
    },

    async logFreightCharge(freightChargeLogDto: IFreightChargeLogDto) {
      await ShoppingCartService.logFreightCharge(freightChargeLogDto);
    },
    async getExxonInfo(){
      return await ShoppingCartService.getExxonInfo();   
   },
  },
});
